import React, { FC } from 'react';
import { Bar, Line, Pie } from 'react-chartjs-2';

import { BrowserProxyStats, CaptchaSolverStats } from '@dreamai/types';

export type { CaptchaSolverStats } from '@dreamai/types';

interface PlatformStatsCaptchaSolverProps {
  stats: CaptchaSolverStats | null | undefined;
  proxy?: BrowserProxyStats | null;
  days: string[];
}

const formatSolveSeconds = (milliseconds: number | null): string =>
  milliseconds == null ? '—' : `${(milliseconds / 1000).toFixed(1)}s`;

const formatPercent = (count: number, total: number): string =>
  total > 0 ? `${((count / total) * 100).toFixed(1)}%` : '—';

const PlatformStatsCaptchaSolver: FC<PlatformStatsCaptchaSolverProps> = ({
  stats,
  proxy,
  days,
}) => {
  const events = stats?.events ?? {
    total: 0,
    solved: 0,
    stillBlocked: 0,
    medianMs: null,
    p90Ms: null,
    perDay: [],
  };
  const escalations = stats?.escalations ?? { total: 0, withSolverEvent: 0 };
  const topHosts = stats?.topHosts ?? [];
  const gate = stats?.gate ?? {
    avoidedEscalation: 0,
    fellThrough: 0,
    flagOn: 0,
    flagOff: 0,
  };
  const tool = stats?.tool;
  const unsolvedReasons = [...(tool?.unsolvedReasons ?? [])].sort(
    (a, b) => b.count - a.count,
  );
  const hasActivity =
    events.total +
      escalations.total +
      gate.avoidedEscalation +
      gate.fellThrough +
      (tool?.calls ?? 0) +
      (proxy?.sessions ?? 0) +
      (proxy?.gb ?? 0) >
    0;
  const flagOnFellThrough = Math.max(0, gate.flagOn - gate.avoidedEscalation);

  const solveRateBadge = (solved: number, calls: number): string =>
    calls > 0 ? `${solved}/${calls} (${formatPercent(solved, calls)})` : '—';

  return (
    <section className='browser-mfa-section-block'>
      <h3>CAPTCHA solver</h3>
      {!hasActivity ? (
        <p className='no-data-placeholder'>
          No CAPTCHA solver activity in this range.
        </p>
      ) : (
        <>
          <div className='stat-badges-row'>
            <span className='stat-badge'>
              Solver events: <strong>{events.total}</strong>
            </span>
            <span className='stat-badge'>
              Solved: <strong>{events.solved}</strong>
            </span>
            <span className='stat-badge'>
              Still blocked: <strong>{events.stillBlocked}</strong>
            </span>
            <span className='stat-badge'>
              Median solve:{' '}
              <strong>{formatSolveSeconds(events.medianMs)}</strong>
            </span>
            <span className='stat-badge'>
              P90 solve: <strong>{formatSolveSeconds(events.p90Ms)}</strong>
            </span>
            <span className='stat-badge'>
              Human CAPTCHA escalations: <strong>{escalations.total}</strong>
            </span>
            <span className='stat-badge'>
              …while solver active:{' '}
              <strong>
                {escalations.withSolverEvent} (
                {formatPercent(escalations.withSolverEvent, escalations.total)})
              </strong>
            </span>
            <span className='stat-badge'>
              Escalations avoided by wait gate:{' '}
              <strong>{gate.avoidedEscalation}</strong>
            </span>
            <span className='stat-badge'>
              Fell through: <strong>{gate.fellThrough}</strong>
            </span>
            <span className='stat-badge'>
              Flag on: <strong>{gate.flagOn}</strong>
            </span>
            <span className='stat-badge'>
              Flag off: <strong>{gate.flagOff}</strong>
            </span>
          </div>

          <div className='chart-card wide'>
            <Line
              data={{
                labels: days,
                datasets: [
                  {
                    label: 'Solver events',
                    data: events.perDay,
                    borderColor: 'rgba(99, 102, 241, 1)',
                    backgroundColor: 'rgba(99, 102, 241, 0.15)',
                    fill: true,
                    tension: 0.25,
                    pointRadius: 2,
                  },
                ],
              }}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  title: {
                    display: true,
                    text: 'CAPTCHA solver events per day',
                    font: {
                      family: 'Instrument Sans, sans-serif',
                      size: 14,
                      weight: 600 as const,
                    },
                    color: '#222',
                  },
                  legend: { display: false },
                },
                scales: {
                  x: { ticks: { font: { size: 10 } } },
                  y: { beginAtZero: true, ticks: { stepSize: 1 } },
                },
              }}
            />
          </div>

          <div className='charts-grid'>
            <div className='chart-card'>
              <Pie
                data={{
                  labels: ['Solved', 'Still blocked'],
                  datasets: [
                    {
                      data: [events.solved, events.stillBlocked],
                      backgroundColor: [
                        'rgba(34, 197, 94, 0.8)',
                        'rgba(239, 68, 68, 0.8)',
                      ],
                      borderWidth: 1,
                      borderColor: '#fff',
                    },
                  ],
                }}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    title: {
                      display: true,
                      text: 'Solver outcomes',
                      font: {
                        family: 'Instrument Sans, sans-serif',
                        size: 13,
                        weight: 600 as const,
                      },
                      color: '#222',
                    },
                    legend: { position: 'bottom' as const },
                  },
                }}
              />
            </div>

            <div className='chart-card'>
              <Bar
                data={{
                  labels: topHosts.map((host) => host.host),
                  datasets: [
                    {
                      label: 'CAPTCHA occurrences',
                      data: topHosts.map((host) => host.count),
                      backgroundColor: 'rgba(14, 165, 233, 0.75)',
                      borderRadius: 3,
                      barPercentage: 0.8,
                    },
                  ],
                }}
                options={{
                  indexAxis: 'y' as const,
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    title: {
                      display: true,
                      text: 'Top CAPTCHA hosts',
                      font: {
                        family: 'Instrument Sans, sans-serif',
                        size: 13,
                        weight: 600 as const,
                      },
                      color: '#222',
                    },
                    legend: { display: false },
                  },
                  scales: {
                    x: { beginAtZero: true, ticks: { stepSize: 1 } },
                    y: { ticks: { font: { size: 10 } } },
                  },
                }}
              />
            </div>

            <div className='chart-card wide'>
              <Bar
                data={{
                  labels: ['Avoided escalation', 'Fell through'],
                  datasets: [
                    {
                      label: 'Flag on',
                      data: [gate.avoidedEscalation, flagOnFellThrough],
                      backgroundColor: 'rgba(34, 197, 94, 0.75)',
                      borderRadius: 3,
                      barPercentage: 0.8,
                    },
                    {
                      label: 'Flag off',
                      data: [0, gate.flagOff],
                      backgroundColor: 'rgba(156, 163, 175, 0.8)',
                      borderRadius: 3,
                      barPercentage: 0.8,
                    },
                  ],
                }}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    title: {
                      display: true,
                      text: 'Wait-gate outcomes by flag',
                      font: {
                        family: 'Instrument Sans, sans-serif',
                        size: 13,
                        weight: 600 as const,
                      },
                      color: '#222',
                    },
                    legend: { position: 'bottom' as const },
                  },
                  scales: {
                    x: { ticks: { font: { size: 10 } } },
                    y: { beginAtZero: true, ticks: { stepSize: 1 } },
                  },
                }}
              />
            </div>
          </div>

          <div className='chart-card wide auto'>
            <div className='breakdown-title'>solveCaptcha tool</div>
            {tool && tool.calls > 0 ? (
              <>
                <div className='stat-badges-row'>
                  <span className='stat-badge'>
                    Calls: <strong>{tool.calls}</strong>
                  </span>
                  <span className='stat-badge'>
                    Solved: <strong>{tool.solved}</strong>
                  </span>
                  <span className='stat-badge'>
                    Unsolved: <strong>{tool.unsolved}</strong>
                  </span>
                  <span className='stat-badge'>
                    Not a CAPTCHA: <strong>{tool.notACaptcha}</strong>
                  </span>
                  <span className='stat-badge'>
                    Median solve:{' '}
                    <strong>{formatSolveSeconds(tool.medianMs)}</strong>
                  </span>
                  <span className='stat-badge'>
                    Solve rate:{' '}
                    <strong>{formatPercent(tool.solved, tool.calls)}</strong>
                  </span>
                  <span className='stat-badge'>
                    Hosted: <strong>{tool.byRunner.hosted}</strong> · B1 arm:{' '}
                    <strong>{tool.byRunner.stagehandArm}</strong>
                  </span>
                  <span className='stat-badge'>
                    Solve rate with proxy:{' '}
                    <strong>
                      {solveRateBadge(
                        tool.byProxy?.proxy.solved ?? 0,
                        tool.byProxy?.proxy.calls ?? 0,
                      )}
                    </strong>{' '}
                    · without:{' '}
                    <strong>
                      {solveRateBadge(
                        tool.byProxy?.noProxy.solved ?? 0,
                        tool.byProxy?.noProxy.calls ?? 0,
                      )}
                    </strong>
                  </span>
                </div>
                {unsolvedReasons.length > 0 && (
                  <div className='outcomes-breakdown'>
                    <div className='breakdown-title'>
                      Unsolved reasons ({tool.unsolved})
                    </div>
                    <table className='outcomes-table'>
                      <thead>
                        <tr>
                          <th>Reason</th>
                          <th>Count</th>
                          <th>% of unsolved</th>
                        </tr>
                      </thead>
                      <tbody>
                        {unsolvedReasons.map((row) => (
                          <tr key={row.reason}>
                            <td>{row.reason}</td>
                            <td>{row.count}</td>
                            <td>{formatPercent(row.count, tool.unsolved)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            ) : (
              <p className='no-data-placeholder'>
                No solveCaptcha tool calls in this range.
              </p>
            )}
          </div>

          <div className='chart-card wide auto'>
            <div className='breakdown-title'>Residential proxy</div>
            {proxy && (proxy.gb > 0 || proxy.sessions > 0) ? (
              <>
                <div className='stat-badges-row'>
                  <span className='stat-badge'>
                    Sessions with proxy: <strong>{proxy.sessions}</strong>
                  </span>
                  <span className='stat-badge'>
                    Proxy GB: <strong>{proxy.gb.toFixed(3)}</strong>
                  </span>
                  <span className='stat-badge'>
                    Proxy cost: <strong>${proxy.usd.toFixed(2)}</strong>
                  </span>
                </div>
                <div className='chart-body'>
                  <Line
                    data={{
                      labels: days,
                      datasets: [
                        {
                          label: 'Proxy GB',
                          data: proxy.perDayGb,
                          borderColor: 'rgba(245, 158, 11, 1)',
                          backgroundColor: 'rgba(245, 158, 11, 0.15)',
                          fill: true,
                          tension: 0.25,
                          pointRadius: 2,
                        },
                      ],
                    }}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        title: {
                          display: true,
                          text: 'Proxy GB per day',
                          font: {
                            family: 'Geist, sans-serif',
                            size: 14,
                            weight: 600 as const,
                          },
                          color: '#222',
                        },
                        legend: { display: false },
                      },
                      scales: {
                        x: { ticks: { font: { size: 10 } } },
                        y: { beginAtZero: true },
                      },
                    }}
                  />
                </div>
              </>
            ) : (
              <p className='no-data-placeholder'>
                No proxy usage in this range.
              </p>
            )}
          </div>
        </>
      )}
    </section>
  );
};

export default PlatformStatsCaptchaSolver;
